import { ReactNode, useState, useMemo, SetStateAction, Dispatch } from 'react';
import BigNumber from 'bignumber.js';

import createSafeContext from '#/utils/createSafeContext';
import { OrderSide, OrderType } from '#/api/orders';
import { PositionSide } from '#/api/positions';
import { useWalletView } from '#/features/wallet/wallet-context';
import { useAccountView } from '#/features/account/account-context';
import { useMarket } from '#/features/options/market-context';
import { Market } from '#/api/markets';

export interface OrderFormState {
  readonly amount: BigNumber | null;
  readonly limitPrice: BigNumber | null;
  readonly type: OrderType;
  readonly side: OrderSide;
}
export interface OrderFormActions {
  readonly updateAmount: (amount: string) => void;
  readonly updateLimitPrice: (limitPrice: string) => void;
  readonly updateType: (type: OrderFormState['type']) => void;
  readonly updateSide: (side: OrderFormState['side']) => void;
  readonly reset: () => void;
}

export const INITIAL_STATE: OrderFormState = {
  amount: null,
  limitPrice: null,
  type: 'LIMIT',
  side: 'BUY',
};

export const [useOrderFormView, ViewCtx] =
  createSafeContext<OrderFormView>('OrderFormView');
export const [useOrderFormActions, ActionsCtx] =
  createSafeContext<OrderFormActions>('OrderFormActions');

interface ProviderProps {
  readonly children: ReactNode;
}

export function OrderFormProvider({ children }: ProviderProps) {
  const activeMarket = useMarket();
  const walletView = useWalletView();
  const [state, setState] = useState(INITIAL_STATE);
  const accountView = useAccountView();

  const actions = useMemo(() => createActions(setState), [setState]);
  const view = useMemo(
    () =>
      createView(
        state,
        activeMarket,
        walletView.isSignedIn,
        accountView.isBeingLiquidated,
      ),
    [state, activeMarket, walletView.isSignedIn, accountView.isBeingLiquidated],
  );

  return (
    <ActionsCtx value={actions}>
      <ViewCtx value={view}>{children}</ViewCtx>
    </ActionsCtx>
  );
}

export interface OrderFormView {
  readonly amount: FieldView;
  readonly limitPrice: FieldView;
  readonly type: OrderType;
  readonly side: OrderSide;
  // Derived:
  readonly isValid: boolean;
  readonly positionSide: PositionSide;
  // Extended:
  readonly baseCurrency: string;
  readonly quoteCurrency: string;
}

interface FormattedView {
  /** raw value
   * @example '30000.5'
   * @example '0.34'
   */
  readonly value: string;
  /**
   * @example '$30,000.50'
   * @example '0.3400 BTC'
   */
  readonly formattedValue: string;
}
interface FieldView extends FormattedView {
  readonly isValid: boolean;
}

// WIP: needs to be covered with unit tests
function createView(
  state: OrderFormState,
  activeMarket: Market,
  isSignedIn: boolean,
  isBeingLiquidated: boolean,
): OrderFormView {
  const amount = amountView(state, activeMarket);
  const limitPrice = limitPriceView(state);
  const positionSide = positionSideView(state);

  const baseCurrency = activeMarket.base_currency;
  const quoteCurrency = activeMarket.quote_currency;

  const isValid =
    !isBeingLiquidated &&
    isSignedIn &&
    [amount, limitPrice].every((field) => field.isValid);

  return {
    type: state.type,
    side: state.side,
    amount,
    limitPrice,
    positionSide,
    baseCurrency,
    quoteCurrency,
    isValid,
  };
}

function limitPriceView({
  limitPrice,
  type,
}: OrderFormState): OrderFormView['limitPrice'] {
  if (type === 'MARKET') {
    return {
      value: '',
      formattedValue: '-',
      isValid: true,
    };
  }

  if (limitPrice == null) {
    return {
      value: '',
      formattedValue: '-',
      isValid: false,
    };
  }

  return {
    value: limitPrice.toString(),
    formattedValue: `$${limitPrice.toFormat()}`,
    isValid: limitPrice.isGreaterThan(0),
  };
}

function amountView(
  { amount }: OrderFormState,
  activeMarket: Market,
): OrderFormView['amount'] {
  if (amount == null) {
    return {
      value: '',
      formattedValue: '-',
      isValid: false,
    };
  }

  return {
    value: amount.toString(),
    formattedValue: `${amount.toFormat()} ${activeMarket.base_currency}`,
    isValid: amount.isGreaterThan(0),
  };
}

function positionSideView({ side }: OrderFormState): PositionSide {
  return side === 'SELL' ? 'SHORT' : 'LONG';
}

export function createActions(
  setState: Dispatch<SetStateAction<OrderFormState>>,
): OrderFormActions {
  return {
    updateAmount(amount: string) {
      const parsed = new BigNumber(amount);
      const parsedWithinLimits = parsed.isFinite()
        ? parsed.isEqualTo('0')
          ? parsed
          : BigNumber.max('0.0002', parsed) // WIP: wire up `market.order_size_increment` to be used here
        : null;

      setState((state) => ({
        ...state,
        amount: parsedWithinLimits,
      }));
    },
    updateLimitPrice(limitPrice: string) {
      const parsed = new BigNumber(limitPrice);
      setState((state) => ({
        ...state,
        limitPrice: parsed.isFinite() ? parsed : null,
      }));
    },
    updateType(type: OrderType) {
      setState((state) => ({ ...state, type }));
    },
    updateSide(side: OrderSide) {
      setState((state) => ({ ...state, side }));
    },
    reset() {
      setState(({ type, side }) => ({ ...INITIAL_STATE, type, side }));
    },
  };
}
